import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Called when the element is connected to the document's DOM.
   */
  connectedCallback() {
    super.connectedCallback();
    // Check initial quantity value after refs are initialized
    // Use requestAnimationFrame to ensure refs are ready
    requestAnimationFrame(() => {
      this.#updateQuantityClass();
    });
  }

  /**
   * Updates the class based on current quantity value.
   */
  #updateQuantityClass() {
    const { quantityInput } = this.refs;
    if (!quantityInput) return;

    const currentValue = parseInt(quantityInput.value);
    let maxValue = quantityInput.max ? parseInt(quantityInput.max) : null;

    // Fallback to data-inventory-quantity if max is not set
    if (!maxValue && quantityInput.dataset.inventoryQuantity) {
      maxValue = parseInt(quantityInput.dataset.inventoryQuantity);
    }

    // Update class for minimum value (1)
    if (currentValue === 1) {
      this.classList.add('has-quantity-1');
    } else {
      this.classList.remove('has-quantity-1');
    }

    // Update class for maximum value
    if (maxValue && currentValue === maxValue) {
      this.classList.add('has-max-quantity');
    } else {
      this.classList.remove('has-max-quantity');
    }
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;

    // Get current and max values
    const currentValue = parseInt(quantityInput.value) || 1;
    let maxValue = quantityInput.max ? parseInt(quantityInput.max) : null;

    // Fallback to data-inventory-quantity if max is not set
    if (!maxValue && quantityInput.dataset.inventoryQuantity) {
      maxValue = parseInt(quantityInput.dataset.inventoryQuantity);
    }

    // Only increase if we haven't reached the maximum
    if (!maxValue || currentValue < maxValue) {
      quantityInput.stepUp();
      this.#onQuantityChange();
    }
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;

    // Get current and min values
    const currentValue = parseInt(quantityInput.value) || 1;
    const minValue = parseInt(quantityInput.min) || 1;

    // Only decrease if we haven't reached the minimum
    if (currentValue > minValue) {
      quantityInput.stepDown();
      this.#onQuantityChange();
    }
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    this.#updateQuantityClass();

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value) || 1;
    const minValue = parseInt(quantityInput.min) || 1;
    let maxValue = quantityInput.max ? parseInt(quantityInput.max) : null;

    // Fallback to data-inventory-quantity if max is not set
    if (!maxValue && quantityInput.dataset.inventoryQuantity) {
      maxValue = parseInt(quantityInput.dataset.inventoryQuantity);
    }

    // Ensure value is within bounds
    if (currentValue < minValue) {
      quantityInput.value = minValue.toString();
    } else if (maxValue && currentValue > maxValue) {
      quantityInput.value = maxValue.toString();
    }
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
